import * as React from 'react';
import { BaseBlockView } from '../base-block.view';
import { HeaderModel } from '../../../model/block';
import styled from 'styled-components';
import { HEADER_VIEW_IN_VIEW_MODE, SMALL_SCREEN_SIZE } from '../../../constants';
import { computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from '../../../interface/component/injected-props.interface';
import { addOpacity } from '../../../util/style/add-opacity.util';
import { determineTextColor } from '../../../util/style/determine-text-color';
import { lightenColor } from '../../../util/style/lighten-color.util';
import { largeMobileMedia } from '../../../util/style/large-mobile-media';
import { SmartLink } from '../../smart-link/smart-link';
import { getLinkFinalURL } from '../../../util/url/get-link-final-url';
import { isStringEmpty } from '../../../util/string/is-string-empty.util';
import {
  ACTIVE_LIGHTNESS,
  HEADER_VIEW_HEIGHT,
  LOGO_IMAGE_PADDING, MENU_ITEM_WIDTH,
  MENU_MIN_WIDTH, NORMAL_MENU_ITEM_HORIZONTAL_MARGIN,
} from './consts';
import { Menu } from './menu';

interface IHeaderMenuContentContainerProps {
  closing: boolean;
  contentHeight: number;
  isSticky: boolean;
  align: 'left' | 'center' | 'right';
}

const HeaderPlaceholder = styled.div`
  height: ${(props: { hasHeight: boolean }) => props.hasHeight ? HEADER_VIEW_HEIGHT : 0}px;
  position: relative;
`;

const NormalHeaderPlaceholder = HeaderPlaceholder;

const DropDownHeaderPlaceholder = HeaderPlaceholder;

interface IHeaderViewContainerProps {
  backgroundColor?: string;
  opacity?: number;
}

const HeaderViewContainerInEditor = styled.div`
  background: ${(props: IHeaderViewContainerProps) => props.backgroundColor ? addOpacity(props.backgroundColor, props.opacity) : 'transparent'};
  height: ${HEADER_VIEW_HEIGHT}px;
  position: relative;
`;

const HeaderViewContainerInView = styled.div<IHeaderViewContainerProps & IHeaderMenuContentContainerProps>`
  background: ${(props) => props.backgroundColor ? addOpacity(props.backgroundColor, props.opacity) : 'transparent'};
  height: ${HEADER_VIEW_HEIGHT}px;
  top: 0;
  left: 0;
  width: ${(props) => props.isSticky ? '100vw' : '100%'};
  z-index: ${HEADER_VIEW_IN_VIEW_MODE};
  position: ${(props) => props.isSticky ? 'fixed' : 'relative'} !important;
`;

const HeaderMenuContainer = styled.div`
  z-index: 1;
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  border-radius: 5px;
  position:relative;
`;

const IMAGE_SIZE = HEADER_VIEW_HEIGHT;


const CenterAlignedLogo = styled.div`
    max-height: ${IMAGE_SIZE - 10}px;
    max-width: calc(100% - ${LOGO_IMAGE_PADDING / 2}px);
    width: auto;
    height: auto;
    margin: 0 auto !important;
    padding-right: ${MENU_MIN_WIDTH}px;
    ${largeMobileMedia(`
      padding-right: 0;
      margin-left: auto !important;
      margin-right: ${LOGO_IMAGE_PADDING / 2}px !important;
    `)}
`;

const RightAlignedLogo = styled.div`
    max-height: ${IMAGE_SIZE - 10}px;
    max-width: calc(100% - ${LOGO_IMAGE_PADDING / 2}px);
    width: auto;
    height: auto;
    margin-left: auto !important;
    margin-right: ${LOGO_IMAGE_PADDING / 2}px !important;
`;

const LeftAlignedLogo = styled.div`
    max-height: ${IMAGE_SIZE - 10}px;
    max-width: calc(100% - ${LOGO_IMAGE_PADDING / 2}px);
    width: auto;
    height: auto;
    margin-right: auto !important;
    margin-left: ${LOGO_IMAGE_PADDING / 2}px !important;
`;

interface ILogoContainerProps {
  backgroundColor: string;
  opacity: number;
  align: 'left' | 'center' | 'right';
}

const alignMap = {
  left: 'flex-start',
  right: 'flex-end',
  center: 'center',
};

const DropdownLogoContainer = styled.div`
  display: flex;
  height: ${HEADER_VIEW_HEIGHT}px;
  align-items: center;
  flex: 1 1 calc(100% - ${MENU_MIN_WIDTH}px);
  width: calc(100% - ${MENU_MIN_WIDTH}px);
  justify-content: ${(props: ILogoContainerProps) => alignMap[props.align]};
  background: ${(props: ILogoContainerProps) => addOpacity(props.backgroundColor, props.opacity)};
`;

const NormalMenuWrapper = styled.div`
  @media (max-width: ${(props: {maxWidth: number}) => props.maxWidth}px) {
    display: none;
  }
`;

const DropdownMenuWrapper = styled.div`
  display: none;
  @media (max-width: ${(props: {maxWidth: number}) => props.maxWidth}px) {
    display: block;
  }
`;

@inject('state', 'dispatch')
@observer
export class HeaderView extends BaseBlockView<HeaderModel, IInjectedProps> {
  @computed
  get hoverTextColor() {
    return this.props.state.colors.getColor(determineTextColor(lightenColor(this.props.state.colors.getColor(this.props.backgroundColor), ACTIVE_LIGHTNESS)));
  }

  @computed
  get textColor() {
    return this.props.state.colors.getColor(determineTextColor(this.props.state.colors.getColor(this.props.backgroundColor)));
  }

  getLogoImage(menuAlignment: 'left' | 'right' | 'center') {
    let LogoImage = this.props.content.menuStyle === 'normal' ? RightAlignedLogo : CenterAlignedLogo;
    if (menuAlignment === 'right') {
      LogoImage = RightAlignedLogo;
    } else if (menuAlignment === 'left') {
      LogoImage = LeftAlignedLogo;
    }
    return (props: any) => {
      const {to, ...others} = props;
      const logo = <LogoImage {...others} />;
      if (isStringEmpty(to)) {
        return logo;
      }
      return <SmartLink to={to}>{logo}</SmartLink>;
    };
  }

  get logoLink() {
    if (!this.props.state.configuration.settings.mainMenu.logo) {
      return '';
    }
    return getLinkFinalURL(this.props.state, this.props.state.configuration.settings.mainMenu.logo.link);
  }

  getMenu(menuStyle: 'dropdown' | 'normal', logoAlignment: 'left' | 'right' | 'center') {
    return <Menu
      items={this.props.state.configuration.settings.mainMenu.items}
      backgroundColor={this.props.state.colors.getColor(this.props.backgroundColor)}
      backgroundOpacity={this.props.content.backgroundOpacity}
      textColor={this.textColor}
      hoverTextColor={this.hoverTextColor}
      inEditor={this.props.inEditor}
      logoAlignment={logoAlignment}
      menuStyle={menuStyle}
    />;
  }

  @computed
  get menuWithDropdown() {
    const {content} = this.props;
    const logoAlignment = this.props.content.menuStyle === 'normal' ? this.props.content.backupLogoAlignment : this.props.content.dropdownLogoAlignment;
    const isLogoLeft = logoAlignment === 'left';
    let ViewContainer = HeaderViewContainerInEditor as any;
    if (!this.props.inEditor) {
      ViewContainer = HeaderViewContainerInView;
    }
    const LogoImage = this.getLogoImage(logoAlignment);
    const logoWithContainer = <DropdownLogoContainer
      backgroundColor={this.props.state.colors.getColor(this.props.backgroundColor)}
      opacity={content.backgroundOpacity}
      align={logoAlignment}
    >
      {content.showLogo && <LogoImage to={this.logoLink} backgroundColor={this.props.backgroundColor}/>}
    </DropdownLogoContainer>;
    return (
      <DropDownHeaderPlaceholder
        data-type="DropDownHeaderPlaceholder"
        hasHeight={this.props.inEditor || !this.props.content.overlayContent || !this.props.isSticky}
      >
        <ViewContainer
          isSticky={this.props.content.overlayContent && !this.props.inEditor}
          data-type="HeaderView"
        >
          <HeaderMenuContainer>
            {isLogoLeft && logoWithContainer}
            {this.getMenu('dropdown', logoAlignment)}
            {!isLogoLeft && logoWithContainer}
          </HeaderMenuContainer>
        </ViewContainer>
      </DropDownHeaderPlaceholder>
    );
  }

  @computed
  get menuNormal() {
    const LogoImage = this.getLogoImage(this.props.content.normalLogoAlignment);
    const isLogoLeft = this.props.content.normalLogoAlignment === 'left';
    let ViewContainer = HeaderViewContainerInEditor as any;
    if (!this.props.inEditor) {
      ViewContainer = HeaderViewContainerInView;
    }
    const logoWithContainer = <DropdownLogoContainer
      backgroundColor={this.props.state.colors.getColor(this.props.backgroundColor)}
      opacity={this.props.content.backgroundOpacity}
      align={this.props.content.normalLogoAlignment}
    >
      {this.props.content.showLogo && <LogoImage to={this.logoLink} backgroundColor={this.props.backgroundColor}/>}
    </DropdownLogoContainer>;
    return <NormalHeaderPlaceholder
      hasHeight={this.props.inEditor || !this.props.content.overlayContent || !this.props.isSticky}
    >
      <ViewContainer
        style={{display: 'flex'}}
        isSticky={this.props.content.overlayContent && !this.props.inEditor}
        data-type="HeaderView"
      >
        {isLogoLeft && logoWithContainer}
        {this.getMenu('normal', this.props.content.normalLogoAlignment)}
        {!isLogoLeft && logoWithContainer}
      </ViewContainer>
    </NormalHeaderPlaceholder>;
  }

  render() {
    if (this.props.content.menuStyle === 'dropdown' || this.props.state.configuration.isMobile) {
      return this.menuWithDropdown;
    }
    const items = this.props.state.configuration.settings.mainMenu.items;
    const maxWidth = (items.length * (MENU_ITEM_WIDTH + 2 * NORMAL_MENU_ITEM_HORIZONTAL_MARGIN)) + HEADER_VIEW_HEIGHT + LOGO_IMAGE_PADDING;
    return <>
      <NormalMenuWrapper data-type="NormalWrapper" maxWidth={Math.max(SMALL_SCREEN_SIZE, maxWidth)}>
        {this.menuNormal}
      </NormalMenuWrapper>
      <DropdownMenuWrapper data-type="DropdownWrapper" maxWidth={Math.max(SMALL_SCREEN_SIZE, maxWidth)}>
        {this.menuWithDropdown}
      </DropdownMenuWrapper>
      </>;
  }
}
